import { parse, type IconDefinition, type Transform } from "@fortawesome/fontawesome-svg-core";

import type { AppIconDefinition, AppIconLayer } from "./Icon";

// Font Awesome draws every glyph on a 16-unit em and states its transforms in that grid.
const em = 16;

/**
 * Projects a single icon into one layer, or returns an existing layer array unchanged.
 */
const layersOf = (definition: AppIconDefinition): readonly AppIconLayer[] =>
    Array.isArray(definition) ? definition : [{ icon: definition as IconDefinition }];

// Font Awesome uses a 16-unit em. Scale both the glyph and its local offset so
// badges remain attached when an entire composition is placed in a collection.
/**
 * Scales a glyph's size and offsets, then adds placement offsets on the 16-unit transform grid.
 *
 * Postconditions: Returns a new transform preserving other properties; string transforms are
 * parsed and parse failures propagate. Scale and offsets are not clamped.
 */
const scaled = (
    transform: AppIconLayer["transform"],
    scale: number,
    x: number,
    y: number
): Transform => {
    const source: Partial<Transform> =
        typeof transform === "string" ? parse.transform(transform) : (transform ?? {});

    return {
        ...source,
        size: (source.size ?? em) * scale,
        x: (source.x ?? 0) * scale + x,
        y: (source.y ?? 0) * scale + y
    };
};

/**
 * Creates positioned copies of each layer, including its optional compact transform.
 *
 * Postconditions: Missing compact transforms stay missing and false stays false; other compact
 * transforms are scaled with the glyph. Input layers are not mutated.
 */
export const place = (
    definition: AppIconDefinition,
    scale: number,
    x: number,
    y: number
): AppIconLayer[] =>
    layersOf(definition).map(layer => ({
        ...layer,
        transform: scaled(layer.transform, scale, x, y),
        // A compact placement travels with its layer, so a whole composition can be scaled into
        // a collection or a badge slot and still know how to shorten itself.
        ...(layer.compact === undefined
            ? {}
            : { compact: layer.compact === false ? false : scaled(layer.compact, scale, x, y) })
    })
    );
